// Centralized helpers to open / download authenticated PDF endpoints.

/** Filename standard per il PDF ordine: NomeCliente_NumeroOrdine.pdf */
export function orderPdfFilename(order, { forCustomer = false } = {}) {
  const cust = order?.customer?.company || order?.customer?.customer_name || order?.customer?.business_name || "";
  const safe = cust
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 20);
  const base = safe ? `${safe}_${order?.order_number || "ordine"}` : `Ordine-${order?.order_number || ""}`;
  return `${base}${forCustomer ? "-cliente" : ""}.pdf`;
}
// - Carries the JWT via axios (a plain window.open(url) would drop the Authorization header → 401).
// - MOBILE-SAFE: the new tab is opened SYNCHRONOUSLY inside the user gesture, BEFORE the async
//   fetch. iOS/Android popup blockers kill window.open() calls made after an await.
// - iOS PWA standalone: blob tabs and the download attribute don't work → native share sheet
//   (from which the user can print, save to Files, AirDrop, etc.).
import axios from "axios";
import { toast } from "sonner";
import { API } from "./format";

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isMobile = () => isIOS() || /Android/i.test(navigator.userAgent);

const isStandalonePWA = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
  window.navigator.standalone === true;

/** Open a blank tab synchronously — MUST be called inside the user gesture (click handler),
 *  before any await, otherwise mobile browsers block it. Returns the window or null. */
export function preparePdfWindow() {
  try {
    const w = window.open("", "_blank");
    if (w && w.document) {
      // Build the loading placeholder via DOM API (not document.write — XSS-safer).
      const doc = w.document;
      doc.title = "Documento";
      const body = doc.body;
      body.style.cssText = "font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#8A1C2A";
      body.textContent = "Caricamento documento…";
    }
    return w;
  } catch {
    return null;
  }
}

async function sharePdf(blob, filename) {
  try {
    const file = new File([blob], filename || "documento.pdf", { type: "application/pdf" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: filename || "Documento PDF" });
      return true;
    }
  } catch (e) {
    if (e?.name === "AbortError") return true; // user dismissed the share sheet — that's fine
  }
  return false;
}

/** Display or download a PDF blob in the most compatible way for the current platform.
 *  opts: { win: pre-opened window | null, download: bool, filename: string } */
export async function showPdfBlob(rawBlob, opts = {}) {
  const { win = null, download = false, filename = "documento.pdf" } = opts;
  const blob = rawBlob?.type === "application/pdf" ? rawBlob : new Blob([rawBlob], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);
  const cleanup = () => setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
  const closeWin = () => {
    try {
      win?.close();
    } catch (e) {
      // Tab già chiusa o non riferibile (cross-origin/preview restrictions). Non bloccante.
      if (process.env.NODE_ENV !== "production") console.debug("[pdf] closeWin noop:", e?.message);
    }
  };

  // App installata su iOS (PWA standalone): blob tabs e download non funzionano → share sheet nativo
  if (isIOS() && isStandalonePWA()) {
    closeWin();
    if (!(await sharePdf(blob, filename))) window.location.assign(blobUrl);
    cleanup();
    return;
  }

  if (download && !isIOS()) {
    closeWin();
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    cleanup();
    return;
  }

  // Visualizzazione inline (su iOS anche per i download: dal viewer si può salvare/stampare)
  if (win && !win.closed) {
    win.location.href = blobUrl;
  } else if (isMobile() && (await sharePdf(blob, filename))) {
    // popup bloccato su mobile → share sheet nativo come fallback
  } else {
    window.location.assign(blobUrl);
  }
  cleanup();
}

async function requestPdf(method, path, body, opts = {}) {
  const url = path.startsWith("http") ? path : `${API}${path.startsWith("/") ? "" : "/"}${path}`;
  // Pre-open the tab inside the user gesture (skip only for desktop downloads)
  const win = opts.download && !isIOS() && !isStandalonePWA() ? null : preparePdfWindow();
  try {
    const r =
      method === "post"
        ? await axios.post(url, body, { responseType: "blob" })
        : await axios.get(url, { responseType: "blob" });
    await showPdfBlob(r.data, { ...opts, win });
    return true;
  } catch (err) {
    try {
      win?.close();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("[pdf] win.close noop:", e?.message);
    }
    let msg = "Errore nel caricamento del PDF";
    if (err?.response?.status === 401) msg = "Sessione scaduta — esegui di nuovo l'accesso";
    else if (err?.response?.data?.detail && typeof err.response.data.detail === "string") msg = err.response.data.detail;
    toast.error(msg);
    return false;
  }
}

/** GET an authenticated PDF endpoint and open (or download) the result.
 *  Returns true on success. opts: { download, filename } */
export async function openAuthenticatedPdf(path, opts = {}) {
  return requestPdf("get", path, null, opts);
}

/** POST to an authenticated PDF endpoint and open (or download) the result.
 *  Returns true on success. opts: { download, filename } */
export async function openAuthenticatedPdfPost(path, body, opts = {}) {
  return requestPdf("post", path, body, opts);
}
